import { useEffect, type ReactElement } from 'react';
import type { ProductKind } from 'types';
import type { ProductAuthoringController } from './controller';
import type { ProductAuthoringCatalogActions, ProductAuthoringProduct } from './types';
import { ProductDrawerFrame, type ProductDrawerFrameContent } from '../../products/ProductDrawerFrame';
import type { ProductDrawerState } from '../../products/drawerState';
import { productKindSection } from '../../products/kindSections';
import { ProductDrawerActions } from '../../products/ProductDrawerActions';
import { productFormValuesFromProduct, type ProductFormValues } from '../../products/formValues';
import type { ProductFormConfig } from '../../products/types';
import { ProductAuthoringForm } from './ProductAuthoringForm';

/** Default create seed: the entry point has already selected the kind. */
export type ProductAuthoringCreateSeed = {
  kind?: ProductKind;
  /** Copy flow: complete form values seeded from an existing product. */
  initialValues?: ProductFormValues;
};

export type ProductAuthoringDrawerProps<TProduct extends ProductAuthoringProduct> = {
  controller: ProductAuthoringController<TProduct>;
  drawer: ProductDrawerState<ProductAuthoringCreateSeed>;
  testId: string;
  formConfig?: ProductFormConfig;
  listPriceEditable?: boolean;
  /** Live catalogue-action wiring; fixture stories omit it. */
  catalogActions?: ProductAuthoringCatalogActions<TProduct>;
};

/**
 * Shared drawer for workspaces that use the default kind lists: resolves the
 * edit target, closes on stale targets, and hosts the shared product form.
 * With live `catalogActions`, the edit form also exposes the catalogue
 * actions (move/convert/remove) and a copy that reopens the drawer seeded
 * from the product.
 */
export function ProductAuthoringDrawer<TProduct extends ProductAuthoringProduct>({
  controller,
  drawer,
  testId,
  formConfig,
  listPriceEditable = true,
  catalogActions,
}: ProductAuthoringDrawerProps<TProduct>): ReactElement {
  const { request, close } = drawer;
  const product =
    request?.mode === 'edit' ? controller.input.products.find((candidate) => candidate.id === request.productId) : null;

  useEffect(() => {
    if (request?.mode === 'edit' && !product) close();
  }, [request, product, close]);

  const seedValues = request?.mode === 'create' ? request.seed?.initialValues : undefined;
  let footerActions: ReactElement | undefined;
  if (request?.mode === 'edit' && product && catalogActions) {
    footerActions = (
      <ProductDrawerActions
        product={product}
        type={product.type}
        endpoint={catalogActions.productEndpoint(product)}
        onClose={close}
        onChanged={catalogActions.onChanged}
        // A moved product either stays in this vendor/type scope with a new
        // outlet or drops out on reconcile; never navigate away from the
        // price-list workspace.
        onMoved={close}
        onCopy={() =>
          drawer.open({
            mode: 'create',
            seed: { kind: product.kind, initialValues: productFormValuesFromProduct(product) },
          })
        }
      />
    );
  }

  let content: ProductDrawerFrameContent | null = null;
  if (request?.mode === 'edit' && product) {
    content = {
      title: `${productKindSection(product.kind).itemTitle} · ${product.name || 'Generic product'}`,
      // Type and kind are immutable after creation; both stay visible on edit.
      subtitle: `${product.type} · ${product.outlet_or_service_category.name}`,
      body: (
        <ProductAuthoringForm
          controller={controller}
          product={product}
          kind={product.kind}
          formConfig={formConfig}
          listPriceEditable={listPriceEditable}
          onClose={close}
          footerActions={footerActions}
        />
      ),
    };
  } else if (request?.mode === 'create') {
    const section = productKindSection(request.seed?.kind ?? 'MEDIA');
    content = {
      title: section.createLabel,
      subtitle: `${controller.input.priceList.type} price list`,
      body: (
        <ProductAuthoringForm
          controller={controller}
          kind={request.seed?.kind ?? 'MEDIA'}
          seedValues={seedValues}
          formConfig={formConfig}
          listPriceEditable={listPriceEditable}
          onClose={close}
        />
      ),
    };
  }

  return <ProductDrawerFrame open={request !== null} onClose={close} content={content} testId={testId} />;
}
